/* eslint-disable @atlassian/i18n/enforce-id-by-package */

import { defineMessages } from 'react-intl';

type MessageDescriptor = {
	id: string;
	defaultMessage: string;
	description: string;
};

/**
 * Note: ALL messages in this folder and file should be marked as `.do-not-translate` so that it is not translated
 */
export const messages: {
	tier1HelperText: MessageDescriptor;
	tier2HelperText: MessageDescriptor;
	tier3HelperText: MessageDescriptor;
	title: MessageDescriptor;
	description: MessageDescriptor;
	instructionsIntro: MessageDescriptor;
	instructionsTier1: MessageDescriptor;
	instructionsTier2: MessageDescriptor;
	instructionsTier3: MessageDescriptor;
	tier1TabLabel: MessageDescriptor;
	tier2TabLabel: MessageDescriptor;
	tier3TabLabel: MessageDescriptor;
	resetButton: MessageDescriptor;
	closePanelLabel: MessageDescriptor;
	datasetSmallOrgLabel: MessageDescriptor;
	datasetSmallOrgDescription: MessageDescriptor;
	datasetMediumOrgLabel: MessageDescriptor;
	datasetMediumOrgDescription: MessageDescriptor;
	datasetLargeOrgLabel: MessageDescriptor;
	datasetLargeOrgDescription: MessageDescriptor;
	datasetTier1PowerUserLabel: MessageDescriptor;
	datasetTier1PowerUserDescription: MessageDescriptor;
	datasetTier1EngineerICLabel: MessageDescriptor;
	datasetTier1EngineerICDescription: MessageDescriptor;
	datasetTier1DesignerICLabel: MessageDescriptor;
	datasetTier1DesignerICDescription: MessageDescriptor;
	datasetTier1ProductLeadLabel: MessageDescriptor;
	datasetTier1ProductLeadDescription: MessageDescriptor;
	datasetTier1NewContributorLabel: MessageDescriptor;
	datasetTier1NewContributorDescription: MessageDescriptor;
	datasetTier3BrandNewLabel: MessageDescriptor;
	datasetTier3BrandNewDescription: MessageDescriptor;
	datasetTier3TrialLabel: MessageDescriptor;
	datasetTier3TrialDescription: MessageDescriptor;
	datasetTier3SoloLabel: MessageDescriptor;
	datasetTier3SoloDescription: MessageDescriptor;
	sentryTestSectionTitle: MessageDescriptor;
	sentryTestSectionHelper: MessageDescriptor;
	sentryTestButtonLabel: MessageDescriptor;
	sentryBoundaryButtonLabel: MessageDescriptor;
	sentryBoundaryFallbackText: MessageDescriptor;
	sentryBoundaryResetButtonLabel: MessageDescriptor;
	viewToggleSectionTitle: MessageDescriptor;
	switchToLoggedOutViewButtonLabel: MessageDescriptor;
	switchToLoggedInViewButtonLabel: MessageDescriptor;
	featureGatesSectionTitle: MessageDescriptor;
	featureGatesSectionHelper: MessageDescriptor;
	featureGatesPricingFaqsLabel: MessageDescriptor;
	featureGatesCliHeaderVariationsLabel: MessageDescriptor;
	featureGatesOptionTrue: MessageDescriptor;
	featureGatesOptionFalse: MessageDescriptor;
	featureGatesOptionControl: MessageDescriptor;
	featureGatesOptionTerminalFirst: MessageDescriptor;
	featureGatesOptionAgentFirst: MessageDescriptor;
	featureGatesResetAllButtonLabel: MessageDescriptor;
	featureGatesResetAllHelper: MessageDescriptor;
} = defineMessages({
	tier1HelperText: {
		id: 'twg-microsite.debugPanel.tier1HelperText.do-not-translate',
		defaultMessage:
			'Pick a Tier 1 dataset to preview the user-scoped view. The home page will render the user graph view seeded with the selected user-metrics payload.',
		description:
			'Helper text shown above the Tier 1 dataset list in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	tier2HelperText: {
		id: 'twg-microsite.debugPanel.tier2HelperText.do-not-translate',
		defaultMessage:
			'Pick a Tier 2 dataset to preview the org-scoped view. The home page will render the org graph view seeded with the selected organisation metrics payload.',
		description:
			'Helper text shown above the Tier 2 dataset list in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	tier3HelperText: {
		id: 'twg-microsite.debugPanel.tier3HelperText.do-not-translate',
		defaultMessage:
			'Pick a Tier 3 scenario to preview the getting-started view. The home page will render the capability showcase with no org metrics data.',
		description: 'Helper text shown above the Tier 3 dataset list in the Debug Panel',
	},
	title: {
		id: 'twg-microsite.debugPanel.title.do-not-translate',
		defaultMessage: 'Data Simulation',
		description: 'Main heading for the Debug Panel',
	},
	description: {
		id: 'twg-microsite.debugPanel.description.do-not-translate',
		defaultMessage:
			'This section allows you to simulate data for different scenarios as different organisations have varying levels of graph density and breadth.',
		description:
			'Description text explaining what the Debug Panel does. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	instructionsIntro: {
		id: 'twg-microsite.debugPanel.instructionsIntro.do-not-translate',
		defaultMessage: 'Choose a tier to preview:',
		description: 'Introductory sentence before listing tier options in the Debug Panel',
	},
	instructionsTier1: {
		id: 'twg-microsite.debugPanel.instructionsTier1.do-not-translate',
		defaultMessage: 'Tier 1 — user-scoped view seeded from a simulated user-metrics payload.',
		description: 'List item describing the Tier 1 preview in the Debug Panel instructions',
	},
	instructionsTier2: {
		id: 'twg-microsite.debugPanel.instructionsTier2.do-not-translate',
		defaultMessage:
			'Tier 2 — org-scoped view seeded from a simulated organisation-metrics payload.',
		description: 'List item describing the Tier 2 preview in the Debug Panel instructions',
	},
	instructionsTier3: {
		id: 'twg-microsite.debugPanel.instructionsTier3.do-not-translate',
		defaultMessage: 'Tier 3 — getting-started capability showcase for early-days tenants.',
		description: 'List item describing the Tier 3 preview in the Debug Panel instructions',
	},
	tier1TabLabel: {
		id: 'twg-microsite.debugPanel.tier1TabLabel.do-not-translate',
		defaultMessage: 'Tier 1',
		description:
			'Tab label for Tier 1 (user-scoped) view previews in the Debug Panel tier switcher',
	},
	tier2TabLabel: {
		id: 'twg-microsite.debugPanel.tier2TabLabel.do-not-translate',
		defaultMessage: 'Tier 2',
		description: 'Tab label for Tier 2 (org-scoped) view previews in the Debug Panel tier switcher',
	},
	tier3TabLabel: {
		id: 'twg-microsite.debugPanel.tier3TabLabel.do-not-translate',
		defaultMessage: 'Tier 3',
		description:
			'Tab label for Tier 3 (getting started) view previews in the Debug Panel tier switcher',
	},
	resetButton: {
		id: 'twg-microsite.debugPanel.resetButton.do-not-translate',
		defaultMessage: 'Reset',
		description: 'Button label that reloads the page and resets the Debug Panel state',
	},
	closePanelLabel: {
		id: 'twg-microsite.debugPanel.closePanelLabel.do-not-translate',
		defaultMessage: 'Close debug panel',
		description: 'Accessibility label for the debug panel close control',
	},
	datasetSmallOrgLabel: {
		id: 'twg-microsite.debugPanel.dataset.smallOrg.label.do-not-translate',
		defaultMessage: 'Small Org',
		description: 'Label for the small static dataset option in the Debug Panel',
	},
	datasetSmallOrgDescription: {
		id: 'twg-microsite.debugPanel.dataset.smallOrg.description.do-not-translate',
		defaultMessage: 'Small tenant with a modest graph and focused activity.',
		description:
			'Description for the small static dataset option in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	datasetMediumOrgLabel: {
		id: 'twg-microsite.debugPanel.dataset.mediumOrg.label.do-not-translate',
		defaultMessage: 'Medium Org',
		description: 'Label for the medium static dataset option in the Debug Panel',
	},
	datasetMediumOrgDescription: {
		id: 'twg-microsite.debugPanel.dataset.mediumOrg.description.do-not-translate',
		defaultMessage: 'Mid-size tenant with heavy Jira usage and strong code linkage.',
		description:
			'Description for the medium static dataset option in the Debug Panel. "Jira" is an Atlassian product name.',
	},
	datasetLargeOrgLabel: {
		id: 'twg-microsite.debugPanel.dataset.largeOrg.label.do-not-translate',
		defaultMessage: 'Large Org',
		description: 'Label for the large static dataset option in the Debug Panel',
	},
	datasetLargeOrgDescription: {
		id: 'twg-microsite.debugPanel.dataset.largeOrg.description.do-not-translate',
		defaultMessage:
			'Very large tenant with the densest graph and highest activity across products.',
		description:
			'Description for the large static dataset option in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	datasetTier1PowerUserLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.powerUser.label.do-not-translate',
		defaultMessage: 'Power user',
		description: 'Label for the Tier 1 "power user" static dataset in the Debug Panel',
	},
	datasetTier1PowerUserDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.powerUser.description.do-not-translate',
		defaultMessage: 'Top of the org across pages, comments, work items, videos, and code.',
		description: 'Description for the Tier 1 "power user" static dataset in the Debug Panel',
	},
	datasetTier1EngineerICLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.engineerIC.label.do-not-translate',
		defaultMessage: 'Engineer (IC)',
		description: 'Label for the Tier 1 "engineer IC" static dataset in the Debug Panel',
	},
	datasetTier1EngineerICDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.engineerIC.description.do-not-translate',
		defaultMessage: 'Heavy code activity — PRs, Jira links, and GitHub events dominate.',
		description:
			'Description for the Tier 1 "engineer IC" static dataset in the Debug Panel. "PRs" means pull requests, "Jira" is an Atlassian product name, and "GitHub" is a third-party developer platform name.',
	},
	datasetTier1DesignerICLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.designerIC.label.do-not-translate',
		defaultMessage: 'Designer (IC)',
		description: 'Label for the Tier 1 "designer IC" static dataset in the Debug Panel',
	},
	datasetTier1DesignerICDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.designerIC.description.do-not-translate',
		defaultMessage: 'Design-heavy profile — strong Figma, Loom, and page activity.',
		description:
			'Description for the Tier 1 "designer IC" static dataset in the Debug Panel. "Figma" is a third-party design product name, and "Loom" is an Atlassian product name.',
	},
	datasetTier1ProductLeadLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.productLead.label.do-not-translate',
		defaultMessage: 'Product lead',
		description: 'Label for the Tier 1 "product lead" static dataset in the Debug Panel',
	},
	datasetTier1ProductLeadDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.productLead.description.do-not-translate',
		defaultMessage: 'Lots of pages, comments, and work items; moderate code and design.',
		description: 'Description for the Tier 1 "product lead" static dataset in the Debug Panel',
	},
	datasetTier1NewContributorLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.newContributor.label.do-not-translate',
		defaultMessage: 'New contributor',
		description: 'Label for the Tier 1 "new contributor" static dataset in the Debug Panel',
	},
	datasetTier1NewContributorDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.newContributor.description.do-not-translate',
		defaultMessage: 'Low volume across all surfaces but just above the Tier 1 floor.',
		description: 'Description for the Tier 1 "new contributor" static dataset in the Debug Panel',
	},
	datasetTier3BrandNewLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier3.brandNewTenant.label.do-not-translate',
		defaultMessage: 'Brand new tenant',
		description: 'Label for the Tier 3 "brand new tenant" static dataset in the Debug Panel',
	},
	datasetTier3BrandNewDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier3.brandNewTenant.description.do-not-translate',
		defaultMessage: 'Freshly provisioned org — nothing connected, no graph activity yet.',
		description:
			'Description for the Tier 3 "brand new tenant" static dataset in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	datasetTier3TrialLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier3.trialTenant.label.do-not-translate',
		defaultMessage: 'Trial tenant',
		description: 'Label for the Tier 3 "trial tenant" static dataset in the Debug Panel',
	},
	datasetTier3TrialDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier3.trialTenant.description.do-not-translate',
		defaultMessage: 'Evaluation tenant with a handful of seats; below the Tier 2 scale gate.',
		description: 'Description for the Tier 3 "trial tenant" static dataset in the Debug Panel',
	},
	datasetTier3SoloLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier3.soloTenant.label.do-not-translate',
		defaultMessage: 'Solo tenant',
		description: 'Label for the Tier 3 "solo tenant" static dataset in the Debug Panel',
	},
	datasetTier3SoloDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier3.soloTenant.description.do-not-translate',
		defaultMessage:
			'Single-user tenant — not enough activity to meet the Tier 2 relationships gate.',
		description: 'Description for the Tier 3 "solo tenant" static dataset in the Debug Panel',
	},
	sentryTestSectionTitle: {
		id: 'twg-microsite.debugPanel.sentryTestSectionTitle.do-not-translate',
		defaultMessage: 'Sentry',
		description:
			'Section header for the Sentry test-exception controls in the Debug Panel. "Sentry" is a third-party application monitoring product name.',
	},
	sentryTestSectionHelper: {
		id: 'twg-microsite.debugPanel.sentryTestSectionHelper.do-not-translate',
		defaultMessage:
			'Throws a test exception and reports it to Sentry via logException. Sentry is disabled in dev environments, so events only appear in the Sentry UI on staging or production.',
		description:
			'Helper text explaining what the Sentry test-exception button does. "Sentry" is a third-party application monitoring product name, and "logException" is a code identifier.',
	},
	sentryTestButtonLabel: {
		id: 'twg-microsite.debugPanel.sentryTestButtonLabel.do-not-translate',
		defaultMessage: 'Trigger test exception',
		description: 'Label for the button that throws a test exception and reports it to Sentry',
	},
	sentryBoundaryButtonLabel: {
		id: 'twg-microsite.debugPanel.sentryBoundaryButtonLabel.do-not-translate',
		defaultMessage: 'Throw render error (boundary)',
		description:
			'Label for the button that throws an error during render to be caught by SentryErrorBoundary',
	},
	sentryBoundaryFallbackText: {
		id: 'twg-microsite.debugPanel.sentryBoundaryFallbackText.do-not-translate',
		defaultMessage: 'SentryErrorBoundary caught a render error and reported it.',
		description:
			'Text shown in the SentryErrorBoundary fallback after a synthetic render error. "SentryErrorBoundary" is a code identifier, the name of a React component. "Sentry" is a third-party error-monitoring product name.',
	},
	sentryBoundaryResetButtonLabel: {
		id: 'twg-microsite.debugPanel.sentryBoundaryResetButtonLabel.do-not-translate',
		defaultMessage: 'Reset boundary',
		description: 'Label for the button that resets the SentryErrorBoundary fallback',
	},
	viewToggleSectionTitle: {
		id: 'twg-microsite.debugPanel.viewToggleSectionTitle.do-not-translate',
		defaultMessage: 'Authentication view',
		description:
			'Section header for the logged-in / logged-out view toggle controls in the Debug Panel.',
	},
	switchToLoggedOutViewButtonLabel: {
		id: 'twg-microsite.debugPanel.switchToLoggedOutViewButtonLabel.do-not-translate',
		defaultMessage: 'Switch to logged out view',
		description:
			'Button label that forces the home page to render the logged-out (marketing) view even when the user is authenticated',
	},
	switchToLoggedInViewButtonLabel: {
		id: 'twg-microsite.debugPanel.switchToLoggedInViewButtonLabel.do-not-translate',
		defaultMessage: 'Switch to logged in view',
		description:
			'Button label that restores the authenticated logged-in view after the debug panel forced the logged-out view',
	},
	featureGatesSectionTitle: {
		id: 'twg-microsite.debugPanel.featureGatesSectionTitle.do-not-translate',
		defaultMessage: 'Feature Gates & Experiments',
		description:
			'Section header for the feature gate and experiment override controls in the Debug Panel',
	},
	featureGatesSectionHelper: {
		id: 'twg-microsite.debugPanel.featureGatesSectionHelper.do-not-translate',
		defaultMessage:
			'Override feature gate and experiment values. Changes persist in localStorage and take effect after a page reload. Use Reset All to restore Switcheroo defaults.',
		description:
			'Helper text explaining how feature gate and experiment overrides work in the Debug Panel. "localStorage" and "Switcheroo" are product/platform names.',
	},
	featureGatesPricingFaqsLabel: {
		id: 'twg-microsite.debugPanel.featureGates.pricingFaqs.label.do-not-translate',
		defaultMessage: 'Pricing FAQs',
		description:
			'Humanized label for the twg_microsite_pricing_faqs feature gate override control in the Debug Panel',
	},
	featureGatesCliHeaderVariationsLabel: {
		id: 'twg-microsite.debugPanel.featureGates.cliHeaderVariations.label.do-not-translate',
		defaultMessage: 'CLI Page Header Variants',
		description:
			'Humanized label for the twg_microsite_cli_page_header_variations experiment override control in the Debug Panel',
	},
	featureGatesOptionTrue: {
		id: 'twg-microsite.debugPanel.featureGates.option.true.do-not-translate',
		defaultMessage: 'true',
		description: 'Option label that overrides a feature gate to true in the Debug Panel',
	},
	featureGatesOptionFalse: {
		id: 'twg-microsite.debugPanel.featureGates.option.false.do-not-translate',
		defaultMessage: 'false',
		description: 'Option label that overrides a feature gate to false in the Debug Panel',
	},
	featureGatesOptionControl: {
		id: 'twg-microsite.debugPanel.featureGates.option.control.do-not-translate',
		defaultMessage: 'control',
		description:
			'Option label that overrides the CLI header variations experiment to the control variant',
	},
	featureGatesOptionTerminalFirst: {
		id: 'twg-microsite.debugPanel.featureGates.option.terminalFirst.do-not-translate',
		defaultMessage: 'terminal_first',
		description:
			'Option label that overrides the CLI header variations experiment to the terminal_first variant',
	},
	featureGatesOptionAgentFirst: {
		id: 'twg-microsite.debugPanel.featureGates.option.agentFirst.do-not-translate',
		defaultMessage: 'agent_first',
		description:
			'Option label that overrides the CLI header variations experiment to the agent_first variant',
	},
	featureGatesResetAllButtonLabel: {
		id: 'twg-microsite.debugPanel.featureGates.resetAllButtonLabel.do-not-translate',
		defaultMessage: 'Reset All',
		description:
			'Button label that clears all feature gate and experiment overrides from localStorage',
	},
	featureGatesResetAllHelper: {
		id: 'twg-microsite.debugPanel.featureGates.resetAllHelper.do-not-translate',
		defaultMessage:
			'Clears ALL feature gate and experiment overrides (including any set outside this panel), then reloads the page.',
		description:
			'Helper text warning that the Reset All button clears every Statsig override, not only those shown in the Debug Panel',
	},
});
